import React, { useMemo } from "react";
import {
	CCAvatar,
	CCCombobox,
	CCComboboxOption,
} from "@pymblecitizencoders/cc-ui";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faArrowRight,
	faCalendar,
	faTriangleExclamation,
	faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { useNoticeManager } from "../../hooks/useNoticeManager";
import { useProfileImages } from "../../hooks/useProfileImages";
import { app } from "../../config";
import { EventDoc, EventNotice } from "../../types/event";
import "./eventManager.css";

interface EventNoticesTabProps {
	event: EventDoc;
	readOnly: boolean;
	onChange: (patch: Partial<EventDoc>) => void;
	/**
	 * Notice ids the server already holds. Anything linked that is not in here
	 * is a link that exists only in the draft, and says so on its card until the
	 * event is saved.
	 */
	savedNoticeIds: Set<string>;
}

/** The API returns Notices as either ids or embedded documents. */
function noticeId(entry: string | EventNotice): string {
	return typeof entry === "string" ? entry : entry._id;
}

function noticeTitle(entry: string | EventNotice): string {
	return typeof entry === "string" ? entry : (entry.Title ?? entry._id);
}

/** An id-only entry has nothing else to draw; everything below copes with that. */
function noticeDoc(entry: string | EventNotice): EventNotice {
	return typeof entry === "string" ? { _id: entry } : entry;
}

/**
 * The banner. `MainImage` is what the notice editor saves and what the event
 * document embeds; `Thumbnail` is the same URL under the name the notice list
 * uses, so either can arrive depending on which end sent the record.
 */
function noticeImage(notice: EventNotice): string | undefined {
	return notice.MainImage || notice.Thumbnail || undefined;
}

/** Published date, whichever of the three names it came under. */
function noticeDate(notice: EventNotice): string {
	const raw = notice.Date || notice.PublishAt || notice.PublishedAt;
	if (!raw) return "";
	const parsed = new Date(raw);
	if (Number.isNaN(parsed.getTime())) return "";
	// "Fri, 15 Dec 23" — the design's format, and the school's date order.
	return parsed.toLocaleDateString("en-AU", {
		weekday: "short",
		day: "numeric",
		month: "short",
		year: "2-digit",
	});
}

/** Author name and contact id, across the slim and the full projection. */
function noticeAuthor(notice: EventNotice): { id?: string; name: string } {
	const info: NonNullable<EventNotice["Author"]> | undefined =
		notice.AuthorDetails || notice.Author;
	if (!info) return { name: "" };
	const name = info.Name || info.PersonInfo?.Name?.FullName || "";
	// Staff records carry a " TEMP" suffix in the source system that is not
	// part of anybody's name — Notice Manager strips it on display too.
	return { id: info._id, name: name.replace(" TEMP", "") };
}

/**
 * Notices tab — search for a notice, then show the linked ones as cards.
 *
 * Two halves, each following something that already exists. The search is a
 * typeahead: matches hang off the input as a dropdown and linking is one
 * choice, rather than type → press Search → read a second column → press +.
 * CCCombobox in its async mode is the kit's own version of that pattern, so the
 * ARIA wiring, arrow keys, Enter to choose and Escape to dismiss come with it
 * instead of being rebuilt here — the same picker the new-event wizard uses for
 * people.
 *
 * What is linked is then pymble-swipe's NoticesTab card: banner, published
 * date, title, summary, author. A notice is recognised by its banner and its
 * author faster than by a title in a list, and the tab is mostly read rather
 * than edited.
 */
export const EventNoticesTab: React.FC<EventNoticesTabProps> = ({
	event,
	readOnly,
	onChange,
	savedNoticeIds,
}) => {
	const {
		setTerm,
		longEnough,
		minQueryLength,
		notices,
		searching,
		searchFailed,
	} = useNoticeManager();
	const { imageUrl } = useProfileImages();

	const linked: (string | EventNotice)[] = useMemo(
		() => (Array.isArray(event.Notices) ? event.Notices : []),
		[event.Notices]
	);

	const linkedIds = useMemo(() => new Set(linked.map(noticeId)), [linked]);

	/**
	 * Linked in the draft, not yet written to the event.
	 *
	 * Worth saying out loud because linking looks finished: the card appears
	 * immediately, the search clears, and nothing on the card would otherwise
	 * distinguish a notice that is attached to the event from one that is a
	 * navigation away from being lost. The Save button knows the draft is dirty,
	 * but it cannot say WHICH notice that is about.
	 */
	const pendingCount = useMemo(
		() => linked.filter((n) => !savedNoticeIds.has(noticeId(n))).length,
		[linked, savedNoticeIds]
	);

	/**
	 * Matches as combobox options, with anything already linked left out rather
	 * than shown ticked: the dropdown closes on choose, so a second choice would
	 * have to mean something, and the cards below are where unlinking lives.
	 */
	const options: CCComboboxOption[] = useMemo(
		() =>
			notices
				.filter((n) => !linkedIds.has(n._id))
				.map((n) => ({
					value: n._id,
					label: n.Title ?? n._id,
					detail: n.Summary,
				})),
		[notices, linkedIds]
	);

	/**
	 * What the empty dropdown says, which is four different situations and one
	 * slot to say them in — "No matches" would be a lie for three of them.
	 */
	const emptyLabel = useMemo(() => {
		if (!longEnough) return `Type at least ${minQueryLength} characters`;
		if (searchFailed) return "Could not run that search";
		if (searching) return "Searching…";
		return notices.length ? "Nothing else matched" : "No notices matched";
	}, [longEnough, minQueryLength, searchFailed, searching, notices.length]);

	/**
	 * The WHOLE search result goes into the draft, not just its id.
	 *
	 * The card below has to draw immediately, and a notice linked a moment ago
	 * has no entry in the event document to draw from — the API resolves
	 * `Notices` on read, which cannot include one that has not been saved yet.
	 * So the draft holds the document.
	 *
	 * The request must not. `Notices` is a list of ids on the way in, and
	 * `toWriteShape` in useEventManager reduces it on the way out. Do not
	 * "fix" this by storing the id here: that empties the card.
	 */
	const addNotice = (id: string) => {
		const notice = notices.find((n) => n._id === id);
		if (!notice || linkedIds.has(id)) return;
		// Cleared alongside the combobox's own input, which it empties on choose
		// without telling us — left set, the next open would show stale matches.
		setTerm("");
		onChange({ Notices: [...linked, notice] });
	};

	const removeNotice = (id: string) =>
		onChange({ Notices: linked.filter((n) => noticeId(n) !== id) });

	/** Notice Manager, on the notice itself — the card's arrow, as in v2. */
	const openNotice = (id: string) =>
		window.open(
			`${app.noticesUrl}?_id=${encodeURIComponent(id)}`,
			"_blank",
			"noopener,noreferrer"
		);

	return (
		<div>
			<h6 className="em-section-heading">Linked Notice</h6>

			{/* `value` stays null on purpose: this adds to the cards below, it is
			    not a single choice the field goes on displaying. */}
			<CCCombobox
				label="Search for notice to link to this event"
				placeholder="Search for notice to link to this event"
				hideLabel
				fullWidth
				className="em-notice-search"
				value={null}
				options={options}
				onChange={addNotice}
				onQueryChange={setTerm}
				loading={searching}
				emptyLabel={emptyLabel}
				disabled={readOnly}
				error={
					searchFailed
						? "Could not run that search. Please try again."
						: undefined
				}
			/>

			<span className="em-notice-count">
				Showing {linked.length} Linked{" "}
				{linked.length === 1 ? "Notice" : "Notices"}
			</span>

			{pendingCount > 0 && (
				<p className="em-notice-pending-note">
					<FontAwesomeIcon icon={faTriangleExclamation} />
					{pendingCount === 1
						? "1 notice is linked but not saved yet."
						: `${pendingCount} notices are linked but not saved yet.`}{" "}
					Choose Save changes to keep{" "}
					{pendingCount === 1 ? "it" : "them"}.
				</p>
			)}

			{linked.length === 0 ? (
				<div className="em-picker-list">
					<div className="em-picker-empty">
						No notices linked to this event yet.
					</div>
				</div>
			) : (
				<div className="em-notice-grid">
					{linked.map((entry) => {
						const notice = noticeDoc(entry);
						const id = notice._id;
						const banner = noticeImage(notice);
						const date = noticeDate(notice);
						const author = noticeAuthor(notice);
						return (
							<div className="em-notice-card" key={id}>
								<div className="em-notice-card__head">
									{banner ? (
										<img
											className="em-notice-card__thumb"
											src={banner}
											alt=""
										/>
									) : (
										<div className="em-notice-card__thumb em-notice-card__thumb--empty">
											P
										</div>
									)}
									{date && (
										<small className="em-notice-card__date">
											<FontAwesomeIcon icon={faCalendar} />
											{date}
										</small>
									)}
								</div>

								<h4
									className="em-notice-card__title"
									title={noticeTitle(entry)}
								>
									{noticeTitle(entry)}
								</h4>
								{notice.Summary && (
									<p
										className="em-notice-card__summary"
										title={notice.Summary}
									>
										{notice.Summary}
									</p>
								)}

								<div className="em-notice-card__foot">
									{author.name && (
										<span className="em-notice-card__author">
											<CCAvatar
												src={imageUrl(author.id)}
												name={author.name}
												size={20}
												decorative
											/>
											<span>{author.name}</span>
										</span>
									)}
									<span className="em-notice-card__actions">
										<button
											type="button"
											className="em-notice-card__action em-notice-card__action--danger"
											disabled={readOnly}
											title="Unlink notice"
											aria-label={`Unlink ${noticeTitle(entry)}`}
											onClick={() => removeNotice(id)}
										>
											<FontAwesomeIcon icon={faXmark} />
										</button>
										<button
											type="button"
											className="em-notice-card__action"
											title="Open notice"
											aria-label={`Open ${noticeTitle(entry)}`}
											onClick={() => openNotice(id)}
										>
											<FontAwesomeIcon icon={faArrowRight} />
										</button>
									</span>
								</div>
							</div>
						);
					})}
				</div>
			)}
		</div>
	);
};
